import React, { useRef } from 'react';
import { Box, Flex } from '@radix-ui/themes';
import { IconSvg } from './IconSvg';
import { CopyButton } from './common/CopyButton';
import { WordInfo } from '../types';
import { REGEX, THEME } from './constants';
import './JapaneseDisplay.css';

interface AudioButtonProps {
  audio?: string;
  isPlaying: boolean;
  onClick: () => void;
}

const AudioButton: React.FC<AudioButtonProps> = ({ audio, isPlaying, onClick }) => {
  if (!audio) return null;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();  // 阻止事件冒泡
    onClick();
  };

  return (
    <button
      className={`audio-button ${isPlaying ? 'playing' : ''}`}
      onClick={handleClick}
      disabled={isPlaying}
    >
      <IconSvg
        name={isPlaying ? "ph:speaker-high-fill" : "ph:speaker-high"}
        width={THEME.SIZES.ICON.DEFAULT}
        height={THEME.SIZES.ICON.DEFAULT}
      />
    </button>
  );
};

interface JapaneseDisplayProps {
  words: WordInfo[];
  translatedText: string;
  onWordClick?: (word: WordInfo, element: HTMLElement, wordKey: string) => void;
  selectedWordKeys?: Set<string>;
}

export const JapaneseDisplay: React.FC<JapaneseDisplayProps> = ({
  words,
  translatedText,
  onWordClick,
  selectedWordKeys = new Set()
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingIndex, setPlayingIndex] = React.useState<number | null>(null);
  const wordRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const handlePlayAudio = (audioUrl: string | undefined, index: number) => {
    if (!audioUrl) return;

    if (audioRef.current) {
      audioRef.current.src = audioUrl;
      audioRef.current.play();
      setPlayingIndex(index);
    }
  };

  React.useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const handleEnded = () => setPlayingIndex(null);
      audio.addEventListener('ended', handleEnded);
      return () => audio.removeEventListener('ended', handleEnded);
    }
  }, []);

  // 获取字符的特性：汉字还是假名或其他
  const getCharType = (char: string): 'kanji' | 'kana' | 'other' => {
    if (REGEX.KANJI.test(char)) return 'kanji';
    if (REGEX.KANA.test(char)) return 'kana';
    return 'other';
  };

  // 获取词组中汉字的数量
  const getKanjiCount = (text: string): number => {
    return text.split('').filter(char => getCharType(char) === 'kanji').length;
  };

  // 提取汉字读音
  const extractReadingForKanji = (word: WordInfo): { kanjiReading: string, isSpecialCase: boolean } => {
    let kanjiReading = '';
    let isSpecialCase = false;

    const kanjiCount = getKanjiCount(word.text);
    const hasKanjiInfo = word.isKanjiWord && word.kanjis && word.kanjis.length > 0;

    if (hasKanjiInfo && kanjiCount === 1 && word.text.length > 1) {
      const fullReading = word.kanjis[0].reading;
      const nonKanjiPart = word.text.substring(1);

      if (word.pos && word.conjugated_form && fullReading.endsWith(nonKanjiPart)) {
        kanjiReading = fullReading.slice(0, fullReading.length - nonKanjiPart.length);
        isSpecialCase = true;
      } else {
        kanjiReading = fullReading;
      }
    } else if (hasKanjiInfo) {
      kanjiReading = word.kanjis[0].reading;
    }

    return { kanjiReading, isSpecialCase };
  };

  const handleWordClick = (word: WordInfo, index: number, wordKey: string) => {
    const element = wordRefs.current.get(index);
    if (element && onWordClick) {
      onWordClick(word, element, wordKey);
    }
  };

  // 生成词卡的唯一键
  const getWordKey = (word: WordInfo, index: number): string => {
    return `${word.text}-${word.pos || 'unknown'}-${index}`;
  };

  return (
    <Box className="japanese-display-container">
      <Flex justify="end" className="display-actions">
        <CopyButton 
          text={words.map(w => w.text).join('')} 
          className="main-copy-button"
          title="复制所有原文"
        />
      </Flex>

      <Flex className="word-container">
        {words.map((word, index) => {
          const { kanjiReading, isSpecialCase } = extractReadingForKanji(word);
          const hasKanjiReading = word.isKanjiWord && word.kanjis && word.kanjis.length > 0;
          const wordKey = getWordKey(word, index);
          const isSelected = selectedWordKeys.has(wordKey);

          return (
            <div
              key={index}
              ref={(el) => {
                if (el) wordRefs.current.set(index, el);
              }}
              className={`word-card ${isSelected ? 'selected' : ''}`}
              onClick={() => handleWordClick(word, index, wordKey)}
              style={{ cursor: onWordClick ? 'pointer' : 'default' }}
              data-word-key={wordKey}
            >
              {isSpecialCase ? (
                <Box className="kanji-text-container">
                  <Box className="kanji-with-furigana">
                    <Box className="furigana-text">{kanjiReading}</Box>
                    <Box className="kanji-only">{word.text.charAt(0)}</Box>
                  </Box>
                  <Box className="kana-part">{word.text.substring(1)}</Box>
                  <AudioButton
                    audio={word.audio}
                    isPlaying={playingIndex === index}
                    onClick={() => handlePlayAudio(word.audio, index)}
                  />
                </Box>
              ) : (
                <>
                  {hasKanjiReading && (
                    <Box className="furigana-text">
                      {kanjiReading}
                    </Box>
                  )}
                  <Box className="kanji-text">
                    {word.text}
                    <AudioButton
                      audio={word.audio}
                      isPlaying={playingIndex === index}
                      onClick={() => handlePlayAudio(word.audio, index)}
                    />
                  </Box>
                </>
              )}
              <div className="romaji-text">
                {word.romaji}
              </div>
            </div>
          );
        })}
      </Flex>

      <Box className="translation-container">
        <Box className="translation-text">{translatedText}</Box>
        <div className="translation-copy-wrapper">
          <CopyButton text={translatedText} size={16} />
        </div>
      </Box>

      <audio ref={audioRef} className="audio-element" />
    </Box>
  );
};